import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Package, MapPin, Navigation, Clock, CheckCircle, 
  Phone, ChevronRight, ArrowUp, ArrowDown, Play, 
  AlertCircle, Truck, RefreshCw, LogOut, Bell,
  Home, List, User, CreditCard, FileText, Settings,
  MessageCircle, Trophy, Star
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { useRegion } from '@/context/RegionContext';
import { useNavigate } from 'react-router-dom';
import api from '@/services/api';
import NotificationBell from '@/components/NotificationBell';
import { InstallAppButton } from '@/components/InstallAppPrompt';
import EntregadorInstallGate from '@/components/EntregadorInstallGate';
import NavegacaoInApp from '@/components/NavegacaoInApp';
import { UpdateBadge, ForceUpdateButton } from '@/components/UpdatePrompt';
import { useGeolocation } from '@/hooks/useGeolocation';
import { useNotifications } from '@/hooks/useNotifications';
import { Volume2, VolumeX } from 'lucide-react';

const AppEntregador = () => {
  const { empresa, usuario, logout, atualizarEmpresa } = useAuth();
  const navigate = useNavigate();
  
  // Estados
  const [entregas, setEntregas] = useState([]);
  const [entregasAbertas, setEntregasAbertas] = useState([]);
  const [entregasEmRota, setEntregasEmRota] = useState([]); // Múltiplas em_rota (até 3 simultâneas)
  const [filaEntregas, setFilaEntregas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('atual'); // 'atual', 'fila', 'disponiveis', 'perfil'
  const MAX_EM_ROTA = 3; // Máximo de entregas em rota simultâneas (stack estilo Uber Eats)
  
  // Modal de finalização
  const [showFinalizarModal, setShowFinalizarModal] = useState(false);
  const [codigoConfirmacao, setCodigoConfirmacao] = useState('');
  const [entregaParaFinalizar, setEntregaParaFinalizar] = useState(null);
  
  // Navegação in-app
  const [destinoNavegacao, setDestinoNavegacao] = useState(null);
  
  // GPS / Localização
  const geo = useGeolocation({ enableHighAccuracy: true });
  const ultimoEnvioGpsRef = useRef(0);
  
  // Som de novo pedido + toggle silenciar
  const { muted, toggleMute, isPlaying, handleEntregaAtendida: silenciarSom } = useNotifications();
  
  // Ranking do entregador
  const [meuRanking, setMeuRanking] = useState(null);
  
  // Carrega dados
  const carregarDados = useCallback(async () => {
    try {
      setRefreshing(true);
      
      // Minhas entregas
      const resMinhas = await api.get('/entregas', { 
        params: { entregador_id: empresa.id } 
      });
      const minhas = resMinhas.data || [];
      setEntregas(minhas);
      
      // Separa entregas em rota (até 3 simultâneas) e fila (aceitas/atribuídas mas não iniciadas)
      const emAndamento = minhas.filter(e => e.status === 'em_rota');
      const aceitas = minhas.filter(e => e.status === 'aceita' || e.status === 'atribuida');
      
      setEntregasEmRota(emAndamento);
      setFilaEntregas(aceitas);
      
      // Entregas disponíveis filtradas (max 3, por proximidade GPS, considerando ranking)
      const resAbertas = await api.get(`/entregas/disponiveis/${empresa.id}`);
      setEntregasAbertas(resAbertas.data || []);
      
      // Ranking do entregador
      try {
        const resRanking = await api.get(`/ranking/${empresa.id}`);
        setMeuRanking(resRanking.data);
      } catch (e) {
        // Ignora se não houver ranking ainda
      }
      
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setRefreshing(false);
    }
  }, [empresa.id]);
  
  useEffect(() => {
    carregarDados();
    // Auto-refresh a cada 30 segundos
    const interval = setInterval(carregarDados, 30000);
    return () => clearInterval(interval);
  }, [carregarDados]);
  
  // Envia GPS ao backend (throttled a cada 30s)
  useEffect(() => {
    if (!geo.latitude || !geo.longitude || !empresa?.id) return;
    
    const agora = Date.now();
    if (agora - ultimoEnvioGpsRef.current < 30000) return;
    ultimoEnvioGpsRef.current = agora;
    
    api.post(`/localizacao/${empresa.id}`, {
      latitude: geo.latitude,
      longitude: geo.longitude,
      precisao: geo.accuracy,
    }).then(() => {
      // Após enviar GPS, recarrega entregas para refletir filtro por proximidade
      carregarDados();
    }).catch(() => {
      // Silencioso: não bloqueia uso do app
    });
  }, [geo.latitude, geo.longitude, geo.accuracy, empresa?.id, carregarDados]);
  
  // Aceitar entrega aberta
  const handleAceitarEntrega = async (entregaId) => {
    setLoading(true);
    try {
      await api.post(`/entregas/${entregaId}/aceitar`, {
        entregador_id: empresa.id
      });
      // Para o som imediatamente (o backend já broadcasta para os outros)
      silenciarSom(entregaId);
      toast.success('Entrega aceita!');
      carregarDados();
      setActiveTab('fila');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao aceitar');
    } finally {
      setLoading(false);
    }
  };
  
  // Iniciar entrega (mover da fila para atual)
  const handleIniciarEntrega = async (entrega) => {
    setLoading(true);
    try {
      await api.patch(`/entregas/${entrega.id}/status`, {
        status: 'em_rota'
      });
      toast.success('Entrega iniciada!');
      carregarDados();
      setActiveTab('atual');
    } catch (error) {
      toast.error('Erro ao iniciar entrega');
    } finally {
      setLoading(false);
    }
  };
  
  // Abrir modal de finalização
  const handleAbrirFinalizacao = (entrega) => {
    setEntregaParaFinalizar(entrega);
    setCodigoConfirmacao('');
    setShowFinalizarModal(true);
  };
  
  // Finalizar entrega com código
  const handleFinalizarEntrega = async () => {
    if (codigoConfirmacao.length !== 6) {
      toast.error('Digite o código de 6 dígitos');
      return;
    }
    
    setLoading(true);
    try {
      await api.post(`/entregas/${entregaParaFinalizar.id}/confirmar`, {
        codigo: codigoConfirmacao
      });
      
      toast.success('Entrega finalizada com sucesso!');
      setShowFinalizarModal(false);
      setCodigoConfirmacao('');
      setEntregaParaFinalizar(null);
      
      // Recarrega e verifica se tem próxima na fila
      await carregarDados();
      
      // Se tem próxima na fila, pergunta se quer iniciar
      if (filaEntregas.length > 0) {
        toast.info('Próxima entrega disponível na fila!');
        setActiveTab('fila');
      }
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Código inválido');
    } finally {
      setLoading(false);
    }
  };
  
  // Reordenar fila
  const moverNaFila = (index, direcao) => {
    const novaFila = [...filaEntregas];
    const novoIndex = index + direcao;
    
    if (novoIndex >= 0 && novoIndex < novaFila.length) {
      [novaFila[index], novaFila[novoIndex]] = [novaFila[novoIndex], novaFila[index]];
      setFilaEntregas(novaFila);
      toast.success('Ordem atualizada');
    }
  };
  
  // Abrir navegação in-app (estilo Uber): mapa + rota + ETA dentro do próprio app.
  // O destino depende do status: 'em_rota' = ir ao cliente (destino); demais = ir à loja (origem).
  const abrirNavegacao = (entrega) => {
    const indoParaCliente = entrega.status === 'em_rota';
    const lat = indoParaCliente ? entrega.destino_latitude : entrega.origem_latitude;
    const lng = indoParaCliente ? entrega.destino_longitude : entrega.origem_longitude;
    const endereco = indoParaCliente ? entrega.destino_endereco : entrega.origem_endereco;
    if (!lat || !lng) {
      toast.error('Coordenadas do endereço indisponíveis');
      return;
    }
    setDestinoNavegacao({
      lat,
      lng,
      endereco,
      label: indoParaCliente ? 'cliente' : 'loja',
    });
  };
  
  // Logout
  const handleLogout = () => {
    logout();
    navigate('/');
  };
  
  // Componente de card de entrega
  const EntregaCard = ({ entrega, tipo, onAction }) => {
    const getStatusColor = (status) => {
      const colors = {
        'aceita': 'bg-blue-100 text-blue-700',
        'em_rota': 'bg-orange-100 text-orange-700',
        'entregue': 'bg-green-100 text-green-700',
        'criada': 'bg-gray-100 text-gray-700'
      };
      return colors[status] || 'bg-gray-100 text-gray-700';
    };
    
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 mb-3">
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Package className="w-5 h-5 text-orange-500" />
            <span className="font-bold text-gray-900">#{entrega.id?.slice(-6)}</span>
          </div>
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(entrega.status)}`}>
            {entrega.status?.replace('_', ' ').toUpperCase()}
          </span>
        </div>
        
        {/* Retirada (Loja / Estabelecimento) — só mostra ANTES do entregador iniciar (status != em_rota) */}
        {entrega.status !== 'em_rota' && (entrega.cliente_nome || entrega.origem_endereco) && (
          <div className="flex items-start gap-2 mb-3 bg-orange-50/60 rounded-lg p-2.5 border border-orange-100" data-testid={`retirada-${entrega.id}`}>
            <div className="w-8 h-8 rounded-full bg-orange-500 flex items-center justify-center flex-shrink-0">
              <Package className="w-4 h-4 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-wide font-semibold text-orange-700">
                Retirada
              </p>
              {entrega.cliente_nome && (
                <p className="text-sm font-bold text-gray-900 truncate" data-testid={`loja-nome-${entrega.id}`}>
                  {entrega.cliente_nome}
                </p>
              )}
              {entrega.origem_endereco && (
                <p className="text-xs text-gray-600 leading-tight" data-testid={`loja-endereco-${entrega.id}`}>
                  {entrega.origem_endereco}
                </p>
              )}
              {entrega.cliente_telefone && (
                <a
                  href={`tel:+${entrega.cliente_telefone}`}
                  className="text-xs text-orange-700 font-medium mt-1 inline-flex items-center gap-1"
                >
                  <Phone className="w-3 h-3" /> {entrega.cliente_telefone}
                </a>
              )}
            </div>
          </div>
        )}
        
        {/* Destino (Cliente Final) */}
        <div className="flex items-start gap-2 mb-3">
          <div className="w-8 h-8 rounded-full bg-red-500 flex items-center justify-center flex-shrink-0">
            <MapPin className="w-4 h-4 text-white" />
          </div>
          <div className="flex-1">
            <p className="text-[10px] uppercase tracking-wide font-semibold text-red-700">
              Entrega
            </p>
            <p className="text-sm font-medium text-gray-900 leading-tight">
              {entrega.destino_endereco || 'Endereço não informado'}
            </p>
            {entrega.destino_bairro && (
              <p className="text-xs text-gray-500">{entrega.destino_bairro}</p>
            )}
            {entrega.distancia_km !== undefined && entrega.distancia_km !== null && (
              <p className="text-xs text-orange-600 font-medium mt-1" data-testid={`entrega-distancia-${entrega.id}`}>
                <Navigation className="w-3 h-3 inline mr-1" />
                {entrega.distancia_km.toFixed(1)} km · ETA {entrega.duracao_min || '—'}min
              </p>
            )}
          </div>
        </div>
        
        {/* Valor */}
        {entrega.valor_recebimento > 0 && (
          <div className="flex items-center gap-2 mb-3 bg-green-50 rounded-lg p-2">
            <CreditCard className="w-4 h-4 text-green-600" />
            <span className="text-sm font-bold text-green-700">
              Receber: R$ {entrega.valor_recebimento?.toFixed(2)}
            </span>
            <span className="text-xs text-green-600 ml-auto">
              {entrega.forma_pagamento === 'dinheiro' ? 'Dinheiro' : 'Cartão'}
            </span>
          </div>
        )}
        
        {/* Descrição */}
        {entrega.descricao && (
          <p className="text-xs text-gray-500 mb-3 bg-gray-50 rounded-lg p-2">
            {entrega.descricao}
          </p>
        )}
        
        {/* Ações */}
        <div className="flex gap-2">
          {tipo === 'disponivel' && (
            <Button 
              onClick={() => onAction(entrega.id)}
              disabled={loading}
              className="flex-1 bg-orange-600 hover:bg-orange-700 h-12 text-base"
            >
              <CheckCircle className="w-5 h-5 mr-2" />
              Aceitar
            </Button>
          )}
          
          {tipo === 'fila' && (
            <>
              <Button 
                onClick={() => onAction(entrega)}
                disabled={loading || entregasEmRota.length >= MAX_EM_ROTA}
                className="flex-1 bg-green-500 hover:bg-green-600 h-12 text-base"
              >
                <Play className="w-5 h-5 mr-2" />
                Iniciar
              </Button>
              <Button 
                variant="outline"
                onClick={() => abrirNavegacao(entrega)}
                className="h-12 px-4"
              >
                <Navigation className="w-5 h-5" />
              </Button>
            </>
          )}
          
          {tipo === 'atual' && (
            <>
              <Button 
                variant="outline"
                onClick={() => abrirNavegacao(entrega)}
                className="flex-1 h-12 text-base"
              >
                <Navigation className="w-5 h-5 mr-2" />
                Rota
              </Button>
              <Button 
                onClick={() => handleAbrirFinalizacao(entrega)}
                className="flex-1 bg-green-500 hover:bg-green-600 h-12 text-base"
              >
                <CheckCircle className="w-5 h-5 mr-2" />
                Finalizar
              </Button>
            </>
          )}
        </div>
      </div>
    );
  };
  
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col max-w-lg mx-auto">
      {/* Modal de instalação para entregadores não-instalados */}
      <EntregadorInstallGate />
      
      {/* Navegação in-app (full-screen) */}
      {destinoNavegacao && (
        <NavegacaoInApp
          destino={destinoNavegacao}
          posicaoAtual={geo.latitude && geo.longitude ? [geo.latitude, geo.longitude] : null}
          onClose={() => setDestinoNavegacao(null)}
        />
      )}
      
      {/* Header Fixo */}
      <header className="bg-gradient-to-r from-orange-600 to-amber-500 text-white px-4 py-3 sticky top-0 z-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo-rota017.png" alt="Rota 017" className="w-10 h-10 rounded-xl object-cover bg-white/20" />
            <div>
              <h1 className="font-bold text-lg">Rota 017</h1>
              <p className="text-xs text-orange-100">{empresa?.nome_fantasia || empresa?.razao_social}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <UpdateBadge />
            <InstallAppButton />
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleMute}
              className={`text-white hover:bg-white/20 ${isPlaying ? 'animate-pulse' : ''}`}
              data-testid="btn-toggle-som"
              title={muted ? 'Som desativado' : 'Som ativo'}
            >
              {muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
            </Button>
            <NotificationBell />
            <Button 
              variant="ghost" 
              size="icon"
              onClick={carregarDados}
              disabled={refreshing}
              className="text-white hover:bg-white/20"
            >
              <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
        
        {/* Stats rápidos */}
        <div className="flex gap-2 mt-3">
          <div className="flex-1 bg-white/20 rounded-xl p-2 text-center">
            <p className="text-2xl font-bold" data-testid="stat-atual-count">{entregasEmRota.length}</p>
            <p className="text-xs text-orange-100">Em Rota</p>
          </div>
          <div className="flex-1 bg-white/20 rounded-xl p-2 text-center">
            <p className="text-2xl font-bold">{filaEntregas.length}</p>
            <p className="text-xs text-orange-100">Na Fila</p>
          </div>
          <div className="flex-1 bg-white/20 rounded-xl p-2 text-center">
            <p className="text-2xl font-bold">{entregasAbertas.length}</p>
            <p className="text-xs text-orange-100">Disponíveis</p>
          </div>
        </div>
        
        {/* Navegação - Tabs no topo */}
        <div className="flex mt-3 bg-white/10 rounded-xl p-1">
          {[
            { id: 'atual', icon: Navigation, label: 'Em Rota', badge: entregasEmRota.length },
            { id: 'fila', icon: List, label: 'Fila', badge: filaEntregas.length },
            { id: 'disponiveis', icon: Package, label: 'Disponíveis', badge: entregasAbertas.length },
            { id: 'perfil', icon: User, label: 'Perfil', badge: 0 }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 px-2 rounded-lg relative transition-all ${
                activeTab === tab.id 
                  ? 'bg-white text-orange-600 shadow-sm font-medium' 
                  : 'text-white/80 hover:bg-white/10'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              <span className="text-xs">{tab.label}</span>
              {tab.badge > 0 && (
                <span className={`absolute -top-1 -right-1 w-5 h-5 text-xs rounded-full flex items-center justify-center ${
                  activeTab === tab.id ? 'bg-red-500 text-white' : 'bg-white text-orange-600'
                }`}>
                  {tab.badge > 9 ? '9+' : tab.badge}
                </span>
              )}
            </button>
          ))}
        </div>
      </header>
      
      {/* Conteúdo */}
      <main className="flex-1 p-4 overflow-auto">
        {/* GPS Status Banner */}
        {!geo.latitude && (
          <div 
            className="bg-yellow-50 border border-yellow-200 rounded-xl p-3 mb-4 flex items-start gap-2"
            data-testid="gps-status-banner"
          >
            <AlertCircle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-yellow-900">
                {geo.error ? 'Localização indisponível' : 'Aguardando GPS...'}
              </p>
              <p className="text-xs text-yellow-700 mt-0.5">
                {geo.error || 'Permita o acesso à localização para receber entregas próximas.'}
              </p>
            </div>
          </div>
        )}
        
        {geo.latitude && (
          <div 
            className="bg-green-50 border border-green-200 rounded-xl p-2 mb-4 flex items-center gap-2"
            data-testid="gps-active-banner"
          >
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
            <p className="text-xs text-green-700 font-medium">
              GPS ativo · precisão {geo.accuracy ? `~${Math.round(geo.accuracy)}m` : 'indisponível'}
            </p>
          </div>
        )}
        {/* Tab: Em Rota (até 3 simultâneas) */}
        {activeTab === 'atual' && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <Navigation className="w-5 h-5 text-orange-500" />
                Entregas em Rota ({entregasEmRota.length}/{MAX_EM_ROTA})
              </h2>
            </div>
            
            {entregasEmRota.length > 0 ? (
              <div className="space-y-3" data-testid="lista-em-rota">
                {entregasEmRota.map((entrega, idx) => (
                  <div key={entrega.id} className="relative">
                    <div className="absolute -left-2 -top-2 w-6 h-6 bg-orange-600 text-white rounded-full flex items-center justify-center text-xs font-bold z-10 shadow">
                      {idx + 1}
                    </div>
                    <EntregaCard entrega={entrega} tipo="atual" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-white rounded-2xl p-8 text-center border border-dashed border-gray-200">
                <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 mb-1">Nenhuma entrega em rota</p>
                <p className="text-sm text-gray-400">
                  {filaEntregas.length > 0 
                    ? `Inicie até ${MAX_EM_ROTA} entregas da sua fila em paralelo`
                    : 'Aceite entregas disponíveis'}
                </p>
                <Button 
                  variant="outline" 
                  className="mt-4"
                  onClick={() => setActiveTab(filaEntregas.length > 0 ? 'fila' : 'disponiveis')}
                >
                  {filaEntregas.length > 0 ? 'Ver Fila' : 'Ver Disponíveis'}
                </Button>
              </div>
            )}
            
            {/* Sugestão de próximas da fila */}
            {entregasEmRota.length < MAX_EM_ROTA && filaEntregas.length > 0 && (
              <div className="mt-4">
                <h3 className="text-sm font-medium text-gray-500 mb-2">
                  Próxima da fila (pode iniciar em paralelo)
                </h3>
                <EntregaCard entrega={filaEntregas[0]} tipo="fila" onAction={handleIniciarEntrega} />
              </div>
            )}
          </div>
        )}
        
        {/* Tab: Fila */}
        {activeTab === 'fila' && (
          <div>
            <h2 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
              <List className="w-5 h-5 text-blue-500" />
              Minha Fila ({filaEntregas.length})
            </h2>
            
            {entregasEmRota.length >= MAX_EM_ROTA && (
              <div className="bg-orange-50 border border-orange-200 rounded-xl p-3 mb-4">
                <p className="text-sm text-orange-700 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  Você atingiu o máximo de {MAX_EM_ROTA} entregas em rota. Finalize uma para iniciar outra.
                </p>
              </div>
            )}
            
            {filaEntregas.length === 0 ? (
              <div className="bg-white rounded-2xl p-8 text-center border border-dashed border-gray-200">
                <List className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">Sua fila está vazia</p>
                <Button 
                  variant="outline" 
                  className="mt-4"
                  onClick={() => setActiveTab('disponiveis')}
                >
                  Ver Entregas Disponíveis
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {filaEntregas.map((entrega, index) => (
                  <div key={entrega.id} className="relative">
                    {/* Controles de ordenação */}
                    <div className="absolute -left-2 top-1/2 -translate-y-1/2 flex flex-col gap-1 z-10">
                      {index > 0 && (
                        <button 
                          onClick={() => moverNaFila(index, -1)}
                          className="w-6 h-6 bg-white rounded-full shadow border flex items-center justify-center"
                        >
                          <ArrowUp className="w-3 h-3 text-gray-600" />
                        </button>
                      )}
                      {index < filaEntregas.length - 1 && (
                        <button 
                          onClick={() => moverNaFila(index, 1)}
                          className="w-6 h-6 bg-white rounded-full shadow border flex items-center justify-center"
                        >
                          <ArrowDown className="w-3 h-3 text-gray-600" />
                        </button>
                      )}
                    </div>
                    
                    {/* Número na fila */}
                    <div className="absolute -right-2 -top-2 w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold z-10">
                      {index + 1}
                    </div>
                    
                    <div className="ml-4">
                      <EntregaCard 
                        entrega={entrega} 
                        tipo="fila" 
                        onAction={handleIniciarEntrega}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        
        {/* Tab: Disponíveis */}
        {activeTab === 'disponiveis' && (
          <div>
            <h2 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
              <Package className="w-5 h-5 text-green-500" />
              Entregas Disponíveis ({entregasAbertas.length})
            </h2>
            
            {entregasAbertas.length === 0 ? (
              <div className="bg-white rounded-2xl p-8 text-center border border-dashed border-gray-200">
                <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">Nenhuma entrega disponível</p>
                <p className="text-sm text-gray-400 mt-1">Novas entregas aparecerão aqui</p>
              </div>
            ) : (
              <div className="space-y-3">
                {entregasAbertas.map(entrega => (
                  <EntregaCard 
                    key={entrega.id} 
                    entrega={entrega} 
                    tipo="disponivel"
                    onAction={handleAceitarEntrega}
                  />
                ))}
              </div>
            )}
          </div>
        )}
        
        {/* Tab: Perfil */}
        {activeTab === 'perfil' && (
          <div>
            <h2 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
              <User className="w-5 h-5 text-purple-500" />
              Meu Perfil
            </h2>
            
            <div className="bg-white rounded-2xl p-4 mb-4">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center">
                  <User className="w-8 h-8 text-orange-500" />
                </div>
                <div>
                  <p className="font-bold text-gray-900">{usuario?.nome}</p>
                  <p className="text-sm text-gray-500">{empresa?.razao_social}</p>
                </div>
              </div>
              
              <div className="space-y-2 text-sm">
                <div className="flex justify-between py-2 border-b">
                  <span className="text-gray-500">Status</span>
                  <span className={`font-medium ${empresa?.assinatura_ativa ? 'text-green-600' : 'text-red-600'}`}>
                    {empresa?.assinatura_ativa ? 'Ativo' : 'Inativo'}
                  </span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="text-gray-500">Validação</span>
                  <span className="font-medium text-gray-900 capitalize">
                    {empresa?.status_validacao?.replace(/_/g, ' ')}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Card de Ranking */}
            {meuRanking && (
              <div 
                className="bg-gradient-to-br from-orange-600 to-amber-500 rounded-2xl p-4 mb-4 text-white"
                data-testid="ranking-card"
              >
                <div className="flex items-center gap-2 mb-3">
                  <Trophy className="w-5 h-5" />
                  <h3 className="font-bold">Meu Ranking</h3>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-white/10 rounded-xl p-3">
                    <p className="text-xs text-orange-100">Score</p>
                    <p className="text-2xl font-bold" data-testid="ranking-score">
                      {meuRanking.score_final?.toFixed(1) || '0.0'}
                    </p>
                  </div>
                  <div className="bg-white/10 rounded-xl p-3">
                    <p className="text-xs text-orange-100">Posição</p>
                    <p className="text-2xl font-bold">
                      #{meuRanking.posicao_ranking || '—'}
                    </p>
                  </div>
                  <div className="bg-white/10 rounded-xl p-3">
                    <p className="text-xs text-orange-100">Estrelas</p>
                    <p className="text-lg font-bold flex items-center gap-1">
                      <Star className="w-4 h-4 fill-yellow-300 text-yellow-300" />
                      {meuRanking.media_estrelas?.toFixed(2) || '—'}
                    </p>
                  </div>
                  <div className="bg-white/10 rounded-xl p-3">
                    <p className="text-xs text-orange-100">Entregas</p>
                    <p className="text-lg font-bold">
                      {meuRanking.total_entregas || 0}
                    </p>
                  </div>
                </div>
                {meuRanking.tempo_medio_minutos > 0 && (
                  <p className="text-xs text-orange-100 mt-3">
                    <Clock className="w-3 h-3 inline mr-1" />
                    Tempo médio: {meuRanking.tempo_medio_minutos.toFixed(0)} min
                  </p>
                )}
              </div>
            )}
            {/* Menu de opções */}
            <div className="bg-white rounded-2xl overflow-hidden mb-4">
              <button 
                onClick={() => navigate('/dashboard/entregador/documentos')}
                className="w-full flex items-center gap-3 p-4 border-b hover:bg-gray-50"
              >
                <FileText className="w-5 h-5 text-gray-400" />
                <span className="flex-1 text-left">Meus Documentos</span>
                <ChevronRight className="w-5 h-5 text-gray-300" />
              </button>
              <button 
                onClick={() => navigate('/dashboard/entregador/endereco')}
                className="w-full flex items-center gap-3 p-4 border-b hover:bg-gray-50"
              >
                <MapPin className="w-5 h-5 text-gray-400" />
                <span className="flex-1 text-left">Meu Endereço</span>
                <ChevronRight className="w-5 h-5 text-gray-300" />
              </button>
              <button 
                onClick={() => navigate('/suporte')}
                className="w-full flex items-center gap-3 p-4 border-b hover:bg-gray-50"
              >
                <MessageCircle className="w-5 h-5 text-orange-500" />
                <span className="flex-1 text-left">Suporte / Fale Conosco</span>
                <ChevronRight className="w-5 h-5 text-gray-300" />
              </button>
              <button 
                onClick={handleLogout}
                className="w-full flex items-center gap-3 p-4 hover:bg-red-50 text-red-600"
              >
                <LogOut className="w-5 h-5" />
                <span className="flex-1 text-left">Sair da Conta</span>
              </button>
            </div>
            
            {/* Botão Forçar Atualização */}
            <ForceUpdateButton />
          </div>
        )}
      </main>
      
      {/* Modal de Finalização */}
      <Dialog open={showFinalizarModal} onOpenChange={setShowFinalizarModal}>
        <DialogContent className="max-w-sm mx-4">
          <DialogHeader>
            <DialogTitle className="text-center">Finalizar Entrega</DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-center text-gray-600 mb-4">
              Digite o código de 6 dígitos fornecido pelo cliente
            </p>
            
            <div className="bg-gray-50 rounded-xl p-4 mb-4">
              <p className="text-xs text-gray-500 mb-1">Entrega</p>
              <p className="font-bold">#{entregaParaFinalizar?.id?.slice(-6)}</p>
              <p className="text-sm text-gray-600 mt-1">
                {entregaParaFinalizar?.destino_endereco}
              </p>
            </div>
            
            <Input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              value={codigoConfirmacao}
              onChange={(e) => setCodigoConfirmacao(e.target.value.replace(/\D/g, ''))}
              placeholder="000000"
              className="text-center text-3xl font-bold tracking-[0.5em] h-16"
              autoFocus
            />
            
            <p className="text-xs text-gray-400 text-center mt-2">
              Este código é sigiloso entre empresa e cliente
            </p>
          </div>
          
          <div className="flex gap-3">
            <Button 
              variant="outline" 
              onClick={() => setShowFinalizarModal(false)}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleFinalizarEntrega}
              disabled={loading || codigoConfirmacao.length !== 6}
              className="flex-1 bg-green-500 hover:bg-green-600"
            >
              {loading ? 'Verificando...' : 'Confirmar'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AppEntregador;
